import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoTip } from './InfoTip'

/** A sortable, paged table — the prototype's Order-history chrome
 * (subscription-portal.html `ORD_COLS`, `ordHeadHTML`, `ordPagerHTML`,
 * `ordPageButtons`), itself copied from the Agent Studio Analytics tables:
 * a chevron per sortable header, and a toolbar above the table with the
 * counter on the left and Reset sort, the page buttons and the per-page
 * switch on the right (MS-820; shared with the MS-821/822 tables).
 *
 * The FULL set is sorted, then paged — "first row" means first overall, not
 * first on this page — and a new sort, page size or reset returns to page 1.
 * Reset sort is inert while the table is already in its default order. */
export interface DataTableColumn<R> {
  key: string
  label: string
  render: (row: R) => ReactNode
  /** Omit to make the column unsortable. */
  sortValue?: (row: R) => string | number
  align?: 'right'
  nowrap?: boolean
  /** An info note beside the label, opening leftwards (the last column). */
  info?: string
}

export interface SortState {
  key: string
  dir: 'asc' | 'desc'
}

const PAGE_SIZES = [10, 20, 50, 100] as const

/** The direction a column's sort button would apply next: the opposite of the
    current one on the sorted column, the default direction on the default
    column, ascending otherwise. */
function nextSortDir(key: string, sort: SortState, defaultSort: SortState): SortState['dir'] {
  if (sort.key === key) return sort.dir === 'asc' ? 'desc' : 'asc'
  return key === defaultSort.key ? defaultSort.dir : 'asc'
}
const PAGE_WINDOW = 3

function Chevron({ dir }: Readonly<{ dir: 'asc' | 'desc' }>) {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === 'asc' ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
    </svg>
  )
}

export function DataTable<R>({
  rows,
  columns,
  rowKey,
  defaultSort,
  showing,
}: Readonly<{
  rows: readonly R[]
  columns: readonly DataTableColumn<R>[]
  rowKey: (row: R) => string
  defaultSort: SortState
  /** The counter, e.g. "Showing 1–10 of 23 orders". */
  showing: (from: number, to: number, total: number) => string
}>) {
  const { t } = useTranslation()
  const [sort, setSort] = useState<SortState>(defaultSort)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0])

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort.key)
    if (!column?.sortValue) return [...rows]
    const value = column.sortValue
    const sign = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = value(a)
      const bv = value(b)
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return cmp * sign
    })
  }, [rows, columns, sort])

  const total = sorted.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(page, totalPages)
  const start = (current - 1) * pageSize
  const pageRows = sorted.slice(start, start + pageSize)
  const isDefault = sort.key === defaultSort.key && sort.dir === defaultSort.dir

  function toggleSort(key: string) {
    setSort((s) => ({ key, dir: nextSortDir(key, s, defaultSort) }))
    setPage(1)
  }

  const from = Math.max(1, Math.min(current - 1, totalPages - PAGE_WINDOW + 1))
  const to = Math.min(totalPages, from + PAGE_WINDOW - 1)
  const pageButton =
    'px-2.5 py-1 border border-[var(--border)] rounded bg-[var(--bg-card)] text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-code)] disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div>
      <div className="flex items-center gap-4 flex-wrap mb-2.5">
        <span className="text-[12px] text-[var(--text-tertiary)]">
          {showing(total === 0 ? 0 : start + 1, start + pageRows.length, total)}
        </span>
        <div className="flex items-center gap-2.5 ml-auto flex-wrap">
          <button
            type="button"
            disabled={isDefault}
            title={t(isDefault ? 'table.resetSortOff' : 'table.resetSortOn')}
            onClick={() => {
              setSort(defaultSort)
              setPage(1)
            }}
            className="h-7 px-2.5 text-[11px] font-semibold rounded-md border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 disabled:cursor-default disabled:hover:border-[var(--border)] disabled:hover:text-[var(--text-secondary)]"
          >
            {t('table.resetSort')}
          </button>
          <div className="flex items-center gap-1">
            <button type="button" className={pageButton} disabled={current === 1} onClick={() => setPage(1)}>
              {t('table.first')}
            </button>
            {from > 1 && (
              <button type="button" className={pageButton} disabled>
                …
              </button>
            )}
            {Array.from({ length: to - from + 1 }, (_, i) => from + i).map((n) => (
              <button
                key={n}
                type="button"
                aria-current={n === current ? 'page' : undefined}
                onClick={() => setPage(n)}
                className={`${pageButton} ${n === current ? '!bg-[var(--accent-subtle)] !text-[var(--accent-text)] !border-[var(--accent-subtle)]' : ''}`}
              >
                {n}
              </button>
            ))}
            {to < totalPages && (
              <button type="button" className={pageButton} disabled>
                …
              </button>
            )}
            <button
              type="button"
              className={pageButton}
              disabled={current === totalPages}
              onClick={() => setPage(totalPages)}
            >
              {t('table.last')}
            </button>
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-[.5px] text-[var(--text-tertiary)]">
            {t('table.perPage')}
          </span>
          <div className="inline-flex h-7 border border-[var(--border)] rounded-md overflow-hidden bg-[var(--bg-card)]">
            {PAGE_SIZES.map((size, i) => (
              <button
                key={size}
                type="button"
                aria-pressed={size === pageSize}
                onClick={() => {
                  setPageSize(size)
                  setPage(1)
                }}
                className={`px-2.5 text-[11px] font-medium ${i > 0 ? 'border-l border-[var(--border-light)]' : ''} ${
                  size === pageSize
                    ? 'bg-[var(--accent-subtle)] text-[var(--accent-text)]'
                    : 'text-[var(--text-tertiary)] hover:bg-[var(--bg-badge)] hover:text-[var(--text-primary)]'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {columns.map((c) => {
                const on = sort.key === c.key
                const nextDir = nextSortDir(c.key, sort, defaultSort)
                const shown = on ? sort.dir : nextDir
                const ariaSort = sort.dir === 'asc' ? 'ascending' : 'descending'
                return (
                  <th
                    key={c.key}
                    aria-sort={on ? ariaSort : undefined}
                    className={`font-semibold text-[11px] uppercase tracking-[.5px] text-[var(--text-tertiary)] px-3 py-2.5 border-b border-[var(--border)] ${
                      c.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2.5 w-full">
                      <span className="inline-flex items-center">
                        {c.label}
                        {c.info && <InfoTip text={c.info} placement="below" align="right" width={300} />}
                      </span>
                      {c.sortValue && (
                        <button
                          type="button"
                          onClick={() => toggleSort(c.key)}
                          title={t(nextDir === 'asc' ? 'table.sortAscending' : 'table.sortDescending')}
                          aria-label={t(nextDir === 'asc' ? 'table.sortAscending' : 'table.sortDescending')}
                          className={`inline-flex items-center p-[3px] rounded-[5px] shrink-0 leading-none transition-colors ${
                            on
                              ? 'text-[var(--accent-text)] bg-[var(--accent-subtle)]'
                              : 'text-[var(--text-tertiary)] hover:bg-[rgba(128,128,128,.14)] hover:text-[var(--text-secondary)]'
                          }`}
                        >
                          <Chevron dir={shown} />
                        </button>
                      )}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={rowKey(row)} className="[&:last-child>td]:border-b-0">
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`p-3 border-b border-[var(--border-light)] align-middle text-[12.5px] ${
                      c.align === 'right' ? 'text-right' : ''
                    } ${c.nowrap ? 'whitespace-nowrap' : ''}`}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
